import type { FC } from "react";

import { Controller, useFormContext } from "@ui/components/Form";
import { MarkdownWrapper } from "@ui/components/MarkdownWrapper";
import { useTranslations } from "@ui/providers/useTranslations";
import { replaceOneTrustPlaceholdersWithLinks } from "@ui/utils/linkReplacer/linkReplacer";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { RadioButton, RadioButtonGroup } from "@jti/core-components";

import styles from "./OptionalConsents.module.scss";

const { sanitize } = dompurify;

type OptionalConsentRadioGroupProps = {
  purposeName: string;
  purposeDescription: string;
  disabled?: boolean;
  defaultValue: boolean | null;
  required?: boolean;
  isColumnDisplayOfConsents?: boolean;
};

export const OptionalConsentRadioGroup: FC<OptionalConsentRadioGroupProps> = ({
  purposeDescription,
  disabled,
  defaultValue,
  purposeName,
  required = true,
  isColumnDisplayOfConsents = false,
}) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const { control } = useFormContext();
  const preparedDefaultValue = defaultValue === null ? null : defaultValue.toString();
  const purposeDescriptionWithLinks = config.ONE_TRUST_DYNAMIC_LINKS
    ? replaceOneTrustPlaceholdersWithLinks(purposeDescription, config.ONE_TRUST_DYNAMIC_LINKS)
    : purposeDescription;

  return (
    <div className={styles.purposeContainer}>
      <div className={styles.radioGroupLabel}>
        <MarkdownWrapper>
          {sanitize(purposeDescriptionWithLinks, {
            ADD_ATTR: ["target"],
          })}
        </MarkdownWrapper>
      </div>
      <Controller
        rules={{
          required: {
            value: required,
            message: __("form_validation.field_required"),
          },
        }}
        defaultValue={preparedDefaultValue}
        name={`purposes.${purposeName}`}
        control={control}
        render={({ field, fieldState }) => (
          <RadioButtonGroup
            name={`purposes.${purposeName}`}
            orientation={isColumnDisplayOfConsents ? "vertical" : "horizontal"}
            isError={!!fieldState.error}
            errorText={fieldState.error?.message}
          >
            {[
              { value: "true", label: __("auth.registration.consents.yes") },
              { value: "false", label: __("auth.registration.consents.no") },
            ].map((opt) => (
              <RadioButton
                key={opt.value}
                value={opt.value}
                label={opt.label}
                checked={field.value === opt.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
              />
            ))}
          </RadioButtonGroup>
        )}
      />
    </div>
  );
};
